import { Component, OnDestroy, OnInit } from "@angular/core";
import { MatDialogRef } from "@angular/material/dialog";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";
import {
  getFulfillmentStageLabelKey,
  getOrderItemsSummary,
} from "../../card-orders/card-order.util";
import { ToastrTranslatedService } from "../../shared/toastr-translated.service";
import { PrintCardOrder } from "../print-card-orders.interface";
import { PrintCardOrdersService } from "../print-card-orders.service";

@Component({
  selector: "app-merge-print-card-orders-dialog",
  templateUrl: "./merge-print-card-orders-dialog.component.html",
  styleUrls: ["./merge-print-card-orders-dialog.component.scss"],
  standalone: false,
})
export class MergePrintCardOrdersDialogComponent implements OnInit, OnDestroy {
  readonly getFulfillmentStageLabelKey = getFulfillmentStageLabelKey;
  orders: PrintCardOrder[] = [];
  selectedIds = new Set<string>();
  primaryOfferId = "";
  loading = true;
  submitting = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly dialogRef: MatDialogRef<
      MergePrintCardOrdersDialogComponent,
      PrintCardOrder | undefined
    >,
    private readonly ordersService: PrintCardOrdersService,
    private readonly toastr: ToastrTranslatedService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.ordersService
      .listMergeCandidates()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (orders) => {
          this.orders = orders;
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.toastr.error("printCardOrders.merge.loadFailed");
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canSubmit(): boolean {
    return this.selectedIds.size >= 2 && Boolean(this.primaryOfferId.trim());
  }

  isSelected(orderId: string): boolean {
    return this.selectedIds.has(orderId);
  }

  isPrimaryOffer(orderId: string): boolean {
    return this.primaryOfferId === orderId;
  }

  toggleOrder(order: PrintCardOrder): void {
    this.setSelected(order, !this.isSelected(order.printCardOrderId));
  }

  setSelected(order: PrintCardOrder, selected: boolean): void {
    const orderId = order.printCardOrderId.trim();
    if (!orderId) {
      return;
    }
    const next = new Set(this.selectedIds);
    if (selected) {
      next.add(orderId);
      if (!this.primaryOfferId) {
        this.primaryOfferId = orderId;
      }
    } else {
      next.delete(orderId);
      if (this.primaryOfferId === orderId) {
        this.primaryOfferId = [...next][0] ?? "";
      }
    }
    this.selectedIds = next;
  }

  setPrimaryOffer(orderId: string, event: Event): void {
    event.stopPropagation();
    const id = orderId.trim();
    if (!id || !this.selectedIds.has(id)) {
      return;
    }
    this.primaryOfferId = id;
  }

  orderItemsSummary(order: PrintCardOrder): string {
    return getOrderItemsSummary(
      order,
      (key, params) => this.translate.instant(key, params) as string,
    );
  }

  close(): void {
    this.dialogRef.close();
  }

  submit(): void {
    if (!this.canSubmit || this.submitting) {
      return;
    }
    this.submitting = true;
    this.ordersService
      .mergeOrders({
        printCardOrderIds: [...this.selectedIds],
        masterPrintCardOrderId: this.primaryOfferId,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (order) => {
          this.submitting = false;
          this.toastr.success("printCardOrders.merge.success");
          this.dialogRef.close(order);
        },
        error: (error: { error?: { error?: string } }) => {
          this.submitting = false;
          const message = error?.error?.error?.trim();
          if (message) {
            this.toastr.error(message);
            return;
          }
          this.toastr.error("printCardOrders.merge.failed");
        },
      });
  }
}
